/**
 * Indicator Renderer (v2 — Web Worker + Incremental Calculation)
 *
 * Manages the lifecycle of lightweight-charts series for active indicators.
 * Creates, updates, and removes series on the chart as indicators are added/removed.
 *
 * Performance optimisations:
 *  1. **Web Worker offloading**: All indicator math runs in a dedicated Web Worker,
 *     keeping the main UI thread completely free for rendering. Falls back to main
 *     thread if the Worker is unavailable.
 *  2. **O(1) incremental calculation**: On live ticks (same candle count or +1),
 *     only a small window of recent candles is recalculated in the worker instead
 *     of the full history. The result is merged with the cached full result.
 *  3. **Live-tick fast render**: series.update() for the last 1–2 points instead
 *     of setData() over the entire history.
 *  4. **Params-change detection** via JSON hash — series torn down and recreated
 *     only when the user modifies indicator params.
 *  5. **Async batching**: multiple indicator updates are dispatched to the worker
 *     in parallel and rendered together when all complete.
 */

import { IChartApi, LineSeries, HistogramSeries, UTCTimestamp, LineWidth, LineStyle, createSeriesMarkers, ISeriesMarkersPluginApi, ISeriesPrimitive } from "lightweight-charts";
import { indicatorEngine } from "../engine/indicator-engine";
import { ActiveIndicator, PlotConfig, PlotData, IndicatorMarker, IndicatorPriceLine, IndicatorBand } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { indicatorWorkerBridge } from "../worker/indicator-worker-bridge";
import { IndicatorShapePrimitive, IndicatorShapeData } from "./indicator-shape-primitives";

// ─── Range Price Line Primitive ───────────────────────────────────────────────
// Draws a dashed horizontal line only from a specific time (entry candle) to the
// right edge of the chart pane — without affecting the time scale.
interface RangePriceLineData {
  fromTime: number;    // UTC seconds (no IST offset) of start candle
  istOffset: number;   // IST offset in seconds applied to chart times
  price: number;
  color: string;
  lineWidth: number;
  lineStyle: number;   // 0=Solid 1=Dotted 2=Dashed
}

class RangePriceLineRenderer {
  private _data: RangePriceLineData;
  private _chart: IChartApi;
  private _series: any;

  constructor(data: RangePriceLineData, chart: IChartApi, series: any) {
    this._data = data;
    this._chart = chart;
    this._series = series;
  }

  update(data: RangePriceLineData) { this._data = data; }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const { context: ctx, bitmapSize, horizontalPixelRatio, verticalPixelRatio } = scope;

      // Convert entry time to x-coordinate (IST-adjusted)
      const entryTimeAdj = (this._data.fromTime + this._data.istOffset) as UTCTimestamp;
      const xMedia = this._chart.timeScale().timeToCoordinate(entryTimeAdj as any);
      if (xMedia === null) return;

      // Convert price to y-coordinate
      const yMedia = this._series.priceToCoordinate(this._data.price);
      if (yMedia === null) return;

      const x = Math.round(xMedia * horizontalPixelRatio);
      const y = Math.round(yMedia * verticalPixelRatio);

      if (y < 0 || y > bitmapSize.height) return;

      ctx.save();
      ctx.strokeStyle = this._data.color;
      ctx.lineWidth = Math.max(1, Math.floor(this._data.lineWidth * horizontalPixelRatio));

      // Set dash pattern based on lineStyle
      if (this._data.lineStyle === 1) {
        // Dotted
        ctx.setLineDash([ctx.lineWidth * 2, ctx.lineWidth * 2]);
      } else if (this._data.lineStyle === 2) {
        // Dashed
        ctx.setLineDash([ctx.lineWidth * 6, ctx.lineWidth * 3]);
      } else {
        // Solid
        ctx.setLineDash([]);
      }

      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(Math.max(0, x), y + 0.5);
      ctx.lineTo(bitmapSize.width, y + 0.5);
      ctx.stroke();
      ctx.restore();
    });
  }
}

class RangePriceLinePrimitive implements ISeriesPrimitive<UTCTimestamp> {
  private _renderer: RangePriceLineRenderer;
  private _view: { renderer: () => RangePriceLineRenderer };
  private _requestUpdate: (() => void) | null = null;

  constructor(data: RangePriceLineData, chart: IChartApi, series: any) {
    this._renderer = new RangePriceLineRenderer(data, chart, series);
    this._view = {
      renderer: () => this._renderer,
    };
  }

  attached(param: any) { this._requestUpdate = param.requestUpdate; }
  detached() { this._requestUpdate = null; }
  updateAllViews() {}
  paneViews() { return [this._view] as any; }
  update(data: RangePriceLineData) { this._renderer.update(data); this._requestUpdate?.(); }
}

// ─── Zone Band Primitive ─────────────────────────────────────────────────────
// Draws filled support/resistance bands (translucent fill + border edges +
// TV-style right-aligned in-band label) from a start time to the right edge
// of the chart pane — one primitive handles all bands of all indicators.
// Bands may also be bounded on the right by `toTime` (e.g. an FVG zone that
// froze when price returned to it) and may use a dotted border.
interface ZoneBandData {
  fromTime: number;    // UTC seconds (no IST offset) of start candle
  toTime?: number;     // UTC seconds of end candle; omit = right chart edge
  istOffset: number;
  top: number;
  bottom: number;
  fill: string;
  border: string;
  lineWidth: number;
  dotted?: boolean;    // dotted border (touched/retired zone)
  label?: string;      // e.g. "RES 4996.275 | 2x" (two-tone: head | tail)
  labelColor?: string;
  labelAlign?: "left" | "right";
  line?: boolean;      // draw as a horizontal line segment at `top` (BOS line)
}

class ZoneBandRenderer {
  private _data: ZoneBandData[];
  private _chart: IChartApi;
  private _series: any;

  constructor(data: ZoneBandData[], chart: IChartApi, series: any) {
    this._data = data;
    this._chart = chart;
    this._series = series;
  }

  update(data: ZoneBandData[]) { this._data = data; }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const { context: ctx, bitmapSize, horizontalPixelRatio, verticalPixelRatio } = scope;
      const width = bitmapSize.width;
      const height = bitmapSize.height;
      const padRight = Math.max(6, Math.round(8 * horizontalPixelRatio));
      const fontPx = Math.max(9, Math.round(11 * verticalPixelRatio));
      const dashPx = Math.max(1, Math.round(1.5 * horizontalPixelRatio));

      ctx.save();
      for (const b of this._data) {
        const xMedia = this._chart.timeScale().timeToCoordinate((b.fromTime + b.istOffset) as UTCTimestamp);
        if (xMedia === null) continue;
        const yTopMedia = this._series.priceToCoordinate(b.top);
        const yBotMedia = this._series.priceToCoordinate(b.bottom);
        if (yTopMedia === null || yBotMedia === null) continue;

        let x = Math.round(xMedia * horizontalPixelRatio);
        const yT = Math.round(yTopMedia * verticalPixelRatio);
        const yB = Math.round(yBotMedia * verticalPixelRatio);
        if (yB < -2 || yT > height + 2) continue;   // fully off-screen vertically
        if (x < 0) x = 0;
        if (x >= width) continue;

        // Right bound: explicit end time, else the right edge of the pane
        let x2 = width;
        if (b.toTime !== undefined && b.toTime !== null) {
          const x2Media = this._chart.timeScale().timeToCoordinate((b.toTime + b.istOffset) as UTCTimestamp);
          if (x2Media === null) continue;
          x2 = Math.min(Math.round(x2Media * horizontalPixelRatio), width);
          if (x2 <= x) continue;
        }

        // Thin horizontal line segment (e.g. a break-of-structure line):
        // single stroke at the band's mid level — no fill, no vertical edges.
        if (b.line) {
          const yLine = Math.round((yT + yB) / 2) + 0.5;
          ctx.strokeStyle = b.border || b.fill;
          ctx.lineWidth = Math.max(1, Math.floor((b.lineWidth || 1) * horizontalPixelRatio));
          ctx.setLineDash(b.dotted ? [dashPx, dashPx] : []);
          ctx.beginPath();
          ctx.moveTo(x, yLine);
          ctx.lineTo(x2, yLine);
          ctx.stroke();
          ctx.setLineDash([]);
          continue;
        }

        // Translucent fill (the zone "shading")
        ctx.fillStyle = b.fill;
        ctx.fillRect(x, yT, x2 - x, yB - yT);

        // Borders: top + bottom edges, left vertical edge, and a right edge
        // only when the band is bounded (frozen) rather than open-ended
        ctx.strokeStyle = b.border;
        ctx.lineWidth = Math.max(1, Math.floor((b.lineWidth || 1) * horizontalPixelRatio));
        ctx.setLineDash(b.dotted ? [dashPx, dashPx] : []);
        ctx.beginPath();
        ctx.moveTo(x, yT + 0.5);
        ctx.lineTo(x2, yT + 0.5);
        ctx.moveTo(x, yB - 0.5);
        ctx.lineTo(x2, yB - 0.5);
        if (x > 0) {
          ctx.moveTo(x + 0.5, yT);
          ctx.lineTo(x + 0.5, yB);
        }
        if (x2 < width) {
          ctx.moveTo(x2 - 0.5, yT);
          ctx.lineTo(x2 - 0.5, yB);
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // TV-style in-band right label: "RES 4996.275 | 2x" (two-tone)
        if (b.label && yB - yT >= fontPx) {
          const midY = (yT + yB) / 2;
          if (midY >= 0 && midY <= height) {
            const sepIdx = b.label.lastIndexOf("|");
            const head = (sepIdx >= 0 ? b.label.slice(0, sepIdx) : b.label).trimEnd();
            const tail = sepIdx >= 0 ? b.label.slice(sepIdx) : "";
            ctx.font = `600 ${fontPx}px Arial, Helvetica, sans-serif`;
            ctx.textBaseline = "middle";
            ctx.textAlign = "left";
            const headW = ctx.measureText(head).width;
            const tailW = tail ? ctx.measureText(tail).width : 0;
            const tx = b.labelAlign === "left"
              ? x + padRight
              : x2 - padRight - headW - tailW;
            ctx.fillStyle = b.labelColor || b.border;
            ctx.fillText(head, tx, midY);
            if (tail) {
              ctx.fillStyle = "rgba(209, 212, 220, 0.95)";
              ctx.fillText(tail, tx + headW, midY);
            }
          }
        }
      }
      ctx.restore();
    });
  }
}

class ZoneBandPrimitive implements ISeriesPrimitive<UTCTimestamp> {
  private _renderer: ZoneBandRenderer;
  private _view: { renderer: () => ZoneBandRenderer };
  private _requestUpdate: (() => void) | null = null;

  constructor(data: ZoneBandData[], chart: IChartApi, series: any) {
    this._renderer = new ZoneBandRenderer(data, chart, series);
    this._view = { renderer: () => this._renderer };
  }

  attached(param: any) { this._requestUpdate = param.requestUpdate; }
  detached() { this._requestUpdate = null; }
  updateAllViews() {}
  paneViews() { return [this._view] as any; }
  update(data: ZoneBandData[]) { this._renderer.update(data); this._requestUpdate?.(); }
}
// ─────────────────────────────────────────────────────────────────────────────

interface RenderedSeries {
  instanceId: string;
  plotKey: string;
  series: any; // ISeriesApi
}

/** Cached computed data for an indicator instance */
interface CachedIndicatorData {
  plotData: PlotData[];
  candleCount: number;
}

export class IndicatorRenderer {
  private chart: IChartApi | null = null;
  private candleSeries: any = null; // main candle series for markers
  private markersPlugin: ISeriesMarkersPluginApi<UTCTimestamp> | null = null;
  private seriesMap: Map<string, RenderedSeries[]> = new Map(); // instanceId -> series[]
  private paramsHash: Map<string, string> = new Map();          // instanceId -> JSON hash of params
  private dataCache: Map<string, CachedIndicatorData> = new Map(); // instanceId -> cached calc results
  private lastCandleCount = 0;
  /** Monotonically increasing version to detect stale async results */
  private updateVersion = 0;
  /** Stored metadata from indicators (e.g. Alpha Trade dashboard) */
  private metadataStore: Map<string, Record<string, any>> = new Map();
  /** Price lines created on the candle series (for Entry/SL/TP levels) */
  private activePriceLines: any[] = []; // IPriceLine[]
  /** Range price line primitives (draw from entry candle to right edge) */
  private activeRangePrimitives: { primitive: RangePriceLinePrimitive; series: any }[] = [];
  /** Filled zone-band primitive (support/resistance boxes) */
  private activeBandsPrimitive: ZoneBandPrimitive | null = null;
  /** Curve bands + reference segments + boxed labels (EMA-band style shapes),
   * one entry per host series (the candle series, plus any sub-pane plot a
   * curve band nominates via `hostPlotKey`) */
  private activeShapesPrimitives: { primitive: IndicatorShapePrimitive; series: any }[] = [];
  /** Last applied layoutSeparatePanes state — used to skip redundant applyOptions calls */
  private lastLayoutKey: string = '';
  /** Per-scale last applied scaleMargins to skip redundant overlay scale updates */
  private lastScaleMargins: Map<string, { top: number; bottom: number }> = new Map();

  attach(chart: IChartApi, candleSeries?: any) {
    this.chart = chart;
    if (candleSeries) {
      this.candleSeries = candleSeries;
      // LWC v5: markers are plugin-based, not series.setMarkers()
      try {
        this.markersPlugin = createSeriesMarkers(candleSeries, []);
      } catch (err) {
        console.warn('[IndicatorRenderer] Failed to create markers plugin:', err);
      }
    }
  }

  detach() {
    this.removeAll();
    indicatorWorkerBridge.cancelAll();
    if (this.markersPlugin) {
      try { this.markersPlugin.detach(); } catch (_) {}
      this.markersPlugin = null;
    }
    this.chart = null;
  }

  /**
   * Main update entry point — now async.
   * Dispatches calculations to the Web Worker and renders results when ready.
   *
   * @param activeIndicators  Current list from React state
   * @param candles           Already timeframe-converted CandleData[]
   * @param istOffset         IST offset in seconds (19800)
   * @param forceFullRecalc   True on backfill, timeframe change, symbol change
   */
  update(
    activeIndicators: ActiveIndicator[],
    candles: CandleData[],
    istOffset: number,
    forceFullRecalc = false,
    onBeforeRender?: () => void,
    onAfterRender?: () => void,
    onScrollGuard?: (locked: boolean) => void,
  ) {
    if (!this.chart || candles.length === 0) return;

    // Bump version so stale async results are discarded
    const thisVersion = ++this.updateVersion;

    // ── 1. Remove series for indicators no longer active ──────────
    const activeIds = new Set(activeIndicators.map(a => a.instanceId));
    const toRemove: string[] = [];
    for (const [instanceId] of this.seriesMap) {
      if (!activeIds.has(instanceId)) {
        toRemove.push(instanceId);
      }
    }
    for (const id of toRemove) {
      this.removeSeries(id);
    }

    // ── 2. Determine update mode ──────────────────────────────────
    const prevCandleCount = this.lastCandleCount;
    const currCandleCount = candles.length;
    this.lastCandleCount = currCandleCount;

    // isLiveTick: same-length (last bar updated in-place) or exactly +1 new bar
    const isLiveTick =
      !forceFullRecalc &&
      prevCandleCount > 0 &&
      (currCandleCount === prevCandleCount || currCandleCount === prevCandleCount + 1);

    // ── 3. Process each active indicator (async via Web Worker) ───
    const indicatorsToCalc: {
      active: ActiveIndicator;
      needFullCalc: boolean;
      isLive: boolean;
    }[] = [];

    for (const active of activeIndicators) {
      if (!active.visible) {
        if (this.seriesMap.has(active.instanceId)) {
          this.removeSeries(active.instanceId);
        }
        continue;
      }

      const def = indicatorEngine.get(active.indicatorId);
      if (!def) continue;

      // Detect param changes
      const currentHash = JSON.stringify(active.params);
      const previousHash = this.paramsHash.get(active.instanceId);
      const paramsChanged = previousHash !== undefined && previousHash !== currentHash;
      if (paramsChanged && this.seriesMap.has(active.instanceId)) {
        this.removeSeries(active.instanceId);
      }
      this.paramsHash.set(active.instanceId, currentHash);

      const isNewSeries = !this.seriesMap.has(active.instanceId);
      const needFullCalc = forceFullRecalc || isNewSeries || paramsChanged;

      indicatorsToCalc.push({ active, needFullCalc, isLive: isLiveTick && !needFullCalc });
    }

    // ── 4. Dispatch all calculations to Worker in parallel ────────
    const calcPromises = indicatorsToCalc.map(({ active, needFullCalc, isLive }) => {
      const cached = this.dataCache.get(active.instanceId);
      const useIncremental = isLive && cached && cached.plotData.length > 0;

      return indicatorWorkerBridge
        .calculate(
          active.indicatorId,
          candles,
          active.params,
          useIncremental || false,
          useIncremental ? cached!.plotData : undefined,
        )
        .then(plotResults => ({
          active,
          plotResults,
          needFullCalc,
          isLive,
        }))
        .catch(err => {
          console.warn(`[IndicatorRenderer] Worker calc failed for ${active.indicatorId}, skipping:`, err);
          return null;
        });
    });

    // ── 5. When all calculations complete, render to chart ────────
    Promise.all(calcPromises).then(results => {
      // Discard stale results if a newer update was triggered
      if (this.updateVersion !== thisVersion) return;
      if (!this.chart) return;

      // Determine if any STRUCTURAL chart modifications will occur
      // (addSeries, setData, layout changes).  Pure series.update() live
      // ticks do NOT drift the chart structurally, but they DO cause LWC
      // internal range adjustments via _correctOffset() — especially for
      // separate-pane indicators.  We must lock the scroll guard for ALL
      // rendering so the range-change handler doesn't capture transient
      // offsets from these internal adjustments.
      const needsViewportRestore = results.some(r => {
        if (!r) return false;
        if (r.needFullCalc) return true;            // will call setData
        if (!this.seriesMap.has(r.active.instanceId)) return true;  // new series
        return false;
      });

      // ALWAYS lock scroll guard before ANY chart modification (including
      // live tick series.update) to prevent the range-change handler from
      // capturing transient offset values during LWC's internal layout
      // recalc.  For structural changes, onBeforeRender also saves the
      // viewport for later restoration.
      if (needsViewportRestore) {
        onBeforeRender?.();
      } else {
        // For live ticks: just lock the scroll guard, no save/restore needed
        onScrollGuard?.(true);
      }

      // Track whether any new non-strategy overlay series are created this cycle.
      // If so, we'll promote strategy series to render on top afterwards.
      let newNonStrategyCreated = false;

      // ── Pass 1: Create all new series (no data yet) ────────────
      for (const result of results) {
        if (!result) continue;
        const { active, plotResults } = result;

        // Cache the result
        this.dataCache.set(active.instanceId, { plotData: plotResults, candleCount: currCandleCount });

        if (plotResults.length === 0) continue;

        const def = indicatorEngine.get(active.indicatorId);
        if (!def) continue;

        if (!this.seriesMap.has(active.instanceId)) {
          const newSeries = this.createSeriesForIndicator(active, def.plots);
          this.seriesMap.set(active.instanceId, newSeries);

          if (def.category !== "strategies") {
            newNonStrategyCreated = true;
          }
        }
      }

      // ── Configure separate-pane layouts BEFORE setData ─────────
      // This ensures that when setData() fires, price scales already
      // have correct scaleMargins, preventing LWC from auto-fitting
      // the time scale.
      this.layoutSeparatePanes(activeIndicators);

      // ── Pass 2: Push data to chart series ──────────────────────
      for (const result of results) {
        if (!result) continue;
        const { active, plotResults, needFullCalc, isLive } = result;

        if (plotResults.length === 0) continue;

        const existingSeries = this.seriesMap.get(active.instanceId);
        if (!existingSeries) continue;

        for (const plotResult of plotResults) {
          const rendered = existingSeries.find(s => s.plotKey === plotResult.key);
          if (!rendered || plotResult.data.length === 0) continue;

          try {
            if (isLive && !needFullCalc) {
              // --- FAST PATH: update only the last data-point(s) ---
              if (currCandleCount === prevCandleCount) {
                // Last bar updated
                const last = plotResult.data[plotResult.data.length - 1];
                rendered.series.update({
                  ...last,
                  time: (last.time + istOffset) as UTCTimestamp,
                });
              } else {
                // One new bar: update previous-last (close it), then update new-last
                if (plotResult.data.length >= 2) {
                  const prevLast = plotResult.data[plotResult.data.length - 2];
                  rendered.series.update({
                    ...prevLast,
                    time: (prevLast.time + istOffset) as UTCTimestamp,
                  });
                }
                const last = plotResult.data[plotResult.data.length - 1];
                rendered.series.update({
                  ...last,
                  time: (last.time + istOffset) as UTCTimestamp,
                });
              }
            } else {
              // --- FULL PATH: setData for the whole history ---
              const chartData = plotResult.data.map(d => ({
                ...d,
                time: (d.time + istOffset) as UTCTimestamp,
              }));
              rendered.series.setData(chartData);
            }
          } catch (err) {
            // Fallback: if update() fails (e.g. time ordering), do setData()
            try {
              const chartData = plotResult.data.map(d => ({
                ...d,
                time: (d.time + istOffset) as UTCTimestamp,
              }));
              rendered.series.setData(chartData);
            } catch (err2) {
              console.error(`[IndicatorRenderer] Error for ${active.indicatorId}.${plotResult.key}:`, err2);
            }
          }
        }
      }

      // ── Z-order: Promote strategy series to render on top ──────
      if (newNonStrategyCreated) {
        for (const result of results) {
          if (!result) continue;
          const def = indicatorEngine.get(result.active.indicatorId);
          if (!def || def.category !== "strategies") continue;

          const oldSeries = this.seriesMap.get(result.active.instanceId);
          if (!oldSeries) continue;

          for (const s of oldSeries) {
            try { this.chart!.removeSeries(s.series); } catch (_) {}
          }

          const fresh = this.createSeriesForIndicator(result.active, def.plots);
          this.seriesMap.set(result.active.instanceId, fresh);

          for (const plotResult of result.plotResults) {
            const rendered = fresh.find(s => s.plotKey === plotResult.key);
            if (rendered && plotResult.data.length > 0) {
              try {
                rendered.series.setData(plotResult.data.map(d => ({
                  ...d,
                  time: (d.time + istOffset) as UTCTimestamp,
                })));
              } catch (_) {}
            }
          }
        }
      }

      // Collect and apply markers from all indicators to the candle series
      this.applyMarkers(results, istOffset);

      // Apply price lines from indicators to the candle series
      this.applyPriceLines(results, istOffset);

      // Apply filled zone bands (support/resistance boxes)
      this.applyZoneBands(results, istOffset);

      // Apply curve bands, reference segments and boxed labels
      this.applyShapes(results, istOffset);

      // Store metadata from indicators for overlay components
      this.storeMetadata(results);

      // For structural changes: restore viewport via rAF cascade.
      // For live ticks: just unlock the scroll guard immediately.
      if (needsViewportRestore) {
        onAfterRender?.();
      } else {
        onScrollGuard?.(false);
      }
    });
  }

  /**
   * Collect markers from all indicator PlotData results and apply them
   * to the main candle series (sorted by time with IST offset applied).
   */
  private applyMarkers(
    results: ({
      active: ActiveIndicator;
      plotResults: PlotData[];
      needFullCalc: boolean;
      isLive: boolean;
    } | null)[],
    istOffset: number,
  ) {
    if (!this.candleSeries) return;

    const allMarkers: IndicatorMarker[] = [];

    for (const result of results) {
      if (!result) continue;
      for (const plotResult of result.plotResults) {
        if (plotResult.markers && plotResult.markers.length > 0) {
          allMarkers.push(...plotResult.markers);
        }
      }
    }

    // Sort markers by time (required by lightweight-charts)
    allMarkers.sort((a, b) => a.time - b.time);

    try {
      if (!this.markersPlugin && this.candleSeries) {
        // Lazily create if not yet initialized
        this.markersPlugin = createSeriesMarkers(this.candleSeries, []);
      }
      if (this.markersPlugin) {
        if (allMarkers.length > 0) {
          this.markersPlugin.setMarkers(
            allMarkers.map(m => ({
              time: (m.time + istOffset) as UTCTimestamp,
              position: m.position,
              color: m.color,
              shape: m.shape,
              text: m.text || "",
              size: m.size || 1,
            })),
          );
        } else {
          // Clear markers when no indicators produce them
          this.markersPlugin.setMarkers([]);
        }
      }
    } catch (err) {
      console.warn("[IndicatorRenderer] Error applying markers:", err);
    }
  }

  /**
   * Apply horizontal price lines (Entry, SL, Target levels) to the candle series.
   * - Lines with `fromTime` are drawn via RangePriceLinePrimitive (entry candle → right edge)
   * - Lines without `fromTime` use createPriceLine (full-width native behavior)
   */
  private applyPriceLines(
    results: ({
      active: ActiveIndicator;
      plotResults: PlotData[];
      needFullCalc: boolean;
      isLive: boolean;
    } | null)[],
    istOffset: number,
  ) {
    if (!this.candleSeries || !this.chart) return;

    // Remove all existing full-width price lines
    for (const pl of this.activePriceLines) {
      try { this.candleSeries.removePriceLine(pl); } catch (_) {}
    }
    this.activePriceLines = [];

    // Remove all existing range price line primitives
    for (const { primitive, series } of this.activeRangePrimitives) {
      try { series.detachPrimitive(primitive); } catch (_) {}
    }
    this.activeRangePrimitives = [];

    // Collect price lines from all indicator results
    const allPriceLines: IndicatorPriceLine[] = [];
    for (const result of results) {
      if (!result) continue;
      for (const plotResult of result.plotResults) {
        if (plotResult.priceLines && plotResult.priceLines.length > 0) {
          allPriceLines.push(...plotResult.priceLines);
        }
      }
    }

    for (const pl of allPriceLines) {
      try {
        if (pl.fromTime != null && pl.lineVisible !== false) {
          // Range line: draw only from entry candle x to right edge via primitive
          // The axis badge is still provided via a no-line createPriceLine
          const textColor = this.getContrastTextColor(pl.color);
          const badge = this.candleSeries.createPriceLine({
            price: pl.price,
            color: pl.color,
            lineWidth: (pl.lineWidth || 2) as LineWidth,
            lineStyle: (pl.lineStyle ?? LineStyle.Dashed) as LineStyle,
            lineVisible: false,  // no full-width line
            axisLabelVisible: pl.axisLabelVisible !== false,
            title: pl.title || "",
            axisLabelColor: pl.color,
            axisLabelTextColor: textColor,
          });
          this.activePriceLines.push(badge);

          // Find the indicator's line series that matches (e.g. "entry" series)
          // Use the candle series as attachment host so it redraws on every tick
          const chart = this.chart;
          const primitive = new RangePriceLinePrimitive(
            {
              fromTime: pl.fromTime,
              istOffset,
              price: pl.price,
              color: pl.color,
              lineWidth: pl.lineWidth || 2,
              lineStyle: pl.lineStyle ?? 2,
            },
            chart,
            this.candleSeries,
          );
          this.candleSeries.attachPrimitive(primitive);
          this.activeRangePrimitives.push({ primitive, series: this.candleSeries });
        } else {
          // Full-width price line (default behavior)
          const textColor = this.getContrastTextColor(pl.color);
          const line = this.candleSeries.createPriceLine({
            price: pl.price,
            color: pl.color,
            lineWidth: (pl.lineWidth || 1) as LineWidth,
            lineStyle: (pl.lineStyle ?? LineStyle.Dashed) as LineStyle,
            lineVisible: pl.lineVisible !== false,
            axisLabelVisible: pl.axisLabelVisible !== false,
            title: pl.title || "",
            axisLabelColor: pl.color,
            axisLabelTextColor: textColor,
          });
          this.activePriceLines.push(line);
        }
      } catch (err) {
        console.warn(`[IndicatorRenderer] Error creating price line ${pl.id}:`, err);
      }
    }
  }

  /**
   * Apply filled zone bands (support/resistance boxes) from indicator results.
   * A single ZoneBandPrimitive attached to the candle series draws every band
   * (translucent fill + borders + in-band right label) each frame.
   */
  private applyZoneBands(
    results: ({
      active: ActiveIndicator;
      plotResults: PlotData[];
      needFullCalc: boolean;
      isLive: boolean;
    } | null)[],
    istOffset: number,
  ) {
    if (!this.candleSeries || !this.chart) return;

    // Detach previous band primitive (bands are fully rebuilt each render)
    if (this.activeBandsPrimitive) {
      try { this.candleSeries.detachPrimitive(this.activeBandsPrimitive); } catch (_) {}
      this.activeBandsPrimitive = null;
    }

    const allBands: IndicatorBand[] = [];
    for (const result of results) {
      if (!result) continue;
      for (const plotResult of result.plotResults) {
        if (plotResult.bands && plotResult.bands.length > 0) {
          allBands.push(...plotResult.bands);
        }
      }
    }
    if (allBands.length === 0) return;

    const data: ZoneBandData[] = allBands.map(b => ({
      fromTime: b.fromTime,
      toTime: b.toTime,
      istOffset,
      top: b.top,
      bottom: b.bottom,
      fill: b.fill,
      border: b.border,
      lineWidth: b.lineWidth ?? 1,
      dotted: b.dotted,
      label: b.label,
      labelColor: b.labelColor,
      labelAlign: b.labelAlign,
      line: b.line,
    }));
    const primitive = new ZoneBandPrimitive(data, this.chart, this.candleSeries);
    this.candleSeries.attachPrimitive(primitive);
    this.activeBandsPrimitive = primitive;
  }

  /**
   * Apply canvas-drawn indicator shapes: shaded curve bands (two moving edges
   * with per-zone fill colours), bounded horizontal reference segments and
   * TradingView-style boxed labels. Bands and segments land on the `bottom`
   * z-layer (behind the candles), labels on `top`.
   *
   * A band may nominate a `hostPlotKey`, in which case it is attached to that
   * plot's own series so it is priced on that plot's scale and drawn in its
   * pane — this is how a sub-pane oscillator gets its 0-based fill. Everything
   * else rides on the candle series. One primitive is mounted per host series.
   */
  private applyShapes(
    results: ({
      active: ActiveIndicator;
      plotResults: PlotData[];
      needFullCalc: boolean;
      isLive: boolean;
    } | null)[],
    istOffset: number,
  ) {
    if (!this.candleSeries || !this.chart) return;

    // Detach the previous primitives (shapes are fully rebuilt each render)
    for (const { primitive, series } of this.activeShapesPrimitives) {
      try { series.detachPrimitive(primitive); } catch (_) {}
    }
    this.activeShapesPrimitives = [];

    const candleBands: IndicatorShapeData["curveBands"] = [];
    const segments: IndicatorShapeData["segments"] = [];
    const labels: IndicatorShapeData["labels"] = [];
    // Bands routed to an indicator's own plot series, keyed by that series.
    const hostedBands = new Map<any, IndicatorShapeData["curveBands"]>();

    for (const result of results) {
      if (!result) continue;
      for (const plotResult of result.plotResults) {
        if (plotResult.segments?.length) {
          segments.push(...plotResult.segments.map(s => ({ ...s, istOffset })));
        }
        if (plotResult.labels?.length) {
          labels.push(...plotResult.labels.map(l => ({ ...l, istOffset })));
        }
        if (!plotResult.curveBands?.length) continue;

        for (const band of plotResult.curveBands) {
          const host = band.hostPlotKey
            ? this.seriesMap.get(result.active.instanceId)?.find(s => s.plotKey === band.hostPlotKey)?.series
            : null;
          if (!host) {
            candleBands.push({ ...band, istOffset });
          } else {
            const bucket = hostedBands.get(host) ?? [];
            bucket.push({ ...band, istOffset });
            hostedBands.set(host, bucket);
          }
        }
      }
    }

    if (candleBands.length === 0 && segments.length === 0 && labels.length === 0 && hostedBands.size === 0) {
      return;
    }

    const mount = (series: any, bands: IndicatorShapeData["curveBands"], segs: IndicatorShapeData["segments"], lbls: IndicatorShapeData["labels"]) => {
      if (bands.length === 0 && segs.length === 0 && lbls.length === 0) return;
      const primitive = new IndicatorShapePrimitive({ curveBands: bands, segments: segs, labels: lbls }, this.chart, series);
      series.attachPrimitive(primitive);
      this.activeShapesPrimitives.push({ primitive, series });
    };

    mount(this.candleSeries, candleBands, segments, labels);
    for (const [series, bands] of hostedBands) {
      mount(series, bands, [], []);
    }
  }

  /**
   * Returns black or white text color based on background luminance.
   * Uses relative luminance formula for WCAG contrast.
   */
  private getContrastTextColor(hexColor: string): string {
    let hex = hexColor.replace(/^#/, "");
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    // Relative luminance (sRGB)
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.45 ? "#000000" : "#FFFFFF";
  }

  /**
   * Extract and store metadata from indicator results for overlay components.
   */
  private storeMetadata(
    results: ({
      active: ActiveIndicator;
      plotResults: PlotData[];
      needFullCalc: boolean;
      isLive: boolean;
    } | null)[],
  ) {
    for (const result of results) {
      if (!result) continue;
      for (const plotResult of result.plotResults) {
        if (plotResult.metadata) {
          this.metadataStore.set(result.active.indicatorId, plotResult.metadata);
        }
      }
    }
  }

  /**
   * Get stored metadata for an indicator (e.g. Alpha Trade dashboard data).
   */
  getMetadata(indicatorId: string): Record<string, any> | undefined {
    return this.metadataStore.get(indicatorId);
  }

  /**
   * True when any of the indicator's plots targets a sub-pane. A plot may
   * override the definition's own `pane`, so an indicator can draw a price
   * overlay and a sub-pane oscillator at the same time.
   */
  private hasSeparatePlot(active: ActiveIndicator): boolean {
    const def = indicatorEngine.get(active.indicatorId);
    if (!def) return false;
    return def.pane === "separate" || def.plots.some(p => p.pane === "separate");
  }

  /**
   * Dynamically distribute separate-pane indicators into non-overlapping
   * vertical regions below the main chart.  Also squeezes the main chart's
   * right price scale to make room.
   */
  private layoutSeparatePanes(activeIndicators: ActiveIndicator[]) {
    if (!this.chart) return;

    // Collect visible separate-pane indicators that have series on the chart
    const separateIndicators: ActiveIndicator[] = [];
    for (const active of activeIndicators) {
      if (!active.visible) continue;
      if (this.hasSeparatePlot(active) && this.seriesMap.has(active.instanceId)) {
        separateIndicators.push(active);
      }
    }

    const n = separateIndicators.length;

    // Build a layout key to detect when the layout actually changes.
    // Format: "n:<count>:<id1>:<id2>:..." — only re-apply when this changes.
    const newLayoutKey = `n:${n}:${separateIndicators.map(a => a.instanceId).join(':')}`;
    const layoutChanged = newLayoutKey !== this.lastLayoutKey;

    if (n === 0) {
      // No separate panes — restore main chart to full height
      if (layoutChanged) {
        try {
          this.chart.priceScale("right").applyOptions({
            scaleMargins: { top: 0.02, bottom: 0.02 },
          });
        } catch (_) {}
        this.lastLayoutKey = newLayoutKey;
        this.lastScaleMargins.clear();
      }
      return;
    }

    const GAP = 0.01;          // 1% gap between panes
    const OUTER_GAP = 0.04;    // 4% gap between main chart and first pane
    const MAX_TOTAL = 0.6;     // separate panes may use at most 60% of chart
    const IDEAL_PANE_H = 0.12; // each pane ideally 12%

    let totalSpace = n * IDEAL_PANE_H + Math.max(0, n - 1) * GAP + OUTER_GAP;
    let paneH = IDEAL_PANE_H;

    if (totalSpace > MAX_TOTAL) {
      totalSpace = MAX_TOTAL;
      paneH = (totalSpace - OUTER_GAP - Math.max(0, n - 1) * GAP) / n;
    }

    // Helper: only call applyOptions when scaleMargins actually changed
    const applyIfChanged = (scaleId: string, top: number, bottom: number, extraOptions?: object) => {
      const prev = this.lastScaleMargins.get(scaleId);
      const topR = Math.round(top * 1000) / 1000;
      const botR = Math.round(bottom * 1000) / 1000;
      if (prev && Math.abs(prev.top - topR) < 0.0001 && Math.abs(prev.bottom - botR) < 0.0001) {
        return; // nothing changed, skip the fullUpdate trigger
      }
      try {
        this.chart!.priceScale(scaleId).applyOptions({
          scaleMargins: { top: topR, bottom: botR },
          ...extraOptions,
        });
        this.lastScaleMargins.set(scaleId, { top: topR, bottom: botR });
      } catch (_) {}
    };

    // Squeeze the main chart to leave room at the bottom
    applyIfChanged("right", 0.02, totalSpace);

    // Position each separate pane in its own vertical slice
    const panesStartY = 1 - totalSpace + OUTER_GAP;

    for (let i = 0; i < n; i++) {
      const active = separateIndicators[i];
      const scaleId = active.instanceId;
      const paneTop = panesStartY + i * (paneH + GAP);
      const paneBottom = 1 - paneTop - paneH;
      applyIfChanged(scaleId, Math.max(0.01, paneTop), Math.max(0.01, paneBottom), { autoScale: true });
    }

    this.lastLayoutKey = newLayoutKey;
  }

  private createSeriesForIndicator(active: ActiveIndicator, plots: PlotConfig[]): RenderedSeries[] {
    if (!this.chart) return [];

    const def = indicatorEngine.get(active.indicatorId);
    const result: RenderedSeries[] = [];

    for (const plot of plots) {
      const color = active.params[`${plot.key}Color`] || active.params.color || plot.color;
      // A `<plotKey>Width` param overrides the plot's declared line width.
      const lineWidth = active.params[`${plot.key}Width`] ?? plot.lineWidth ?? 2;
      // A plot may opt out of the definition's pane (e.g. a price overlay that
      // also carries a sub-pane oscillator); only the sub-pane plots get their
      // own price scale, everything else stays on the main right scale.
      const scaleId = (plot.pane ?? def?.pane) === "separate" ? active.instanceId : undefined;

      let series: any;

      try {
        if (plot.style === "histogram") {
          series = this.chart.addSeries(HistogramSeries, {
            color,
            priceScaleId: scaleId,
            base: plot.histogramBase ?? 0,
            priceFormat: { type: "price" as any, precision: 2, minMove: 0.01 },
            lastValueVisible: false,
            priceLineVisible: false,
          });
        } else if (plot.style === "circles") {
          // Render as individual dots: hide the connecting line, show point markers only
          // Use per-indicator dotSize param if provided, otherwise fall back to lineWidth-based sizing
          const dotRadius = active.params.dotSize != null
            ? Math.max(1, Math.round(active.params.dotSize))
            : Math.max(2, (plot.lineWidth || 2) * 2);
          series = this.chart.addSeries(LineSeries, {
            color,
            lineVisible: false,
            pointMarkersVisible: true,
            pointMarkersRadius: dotRadius,
            crosshairMarkerVisible: false,
            priceScaleId: scaleId,
            lastValueVisible: plot.lastValueVisible ?? false,
            priceLineVisible: plot.priceLineVisible ?? false,
            priceFormat: { type: "price" as any, precision: 2, minMove: 0.01 },
          });
        } else {
          series = this.chart.addSeries(LineSeries, {
            color,
            lineVisible: plot.lineVisible ?? true,
            lineWidth: Math.max(1, Math.min(6, Math.round(lineWidth))) as LineWidth,
            lineStyle: (plot.lineStyle ?? 0) as LineStyle,
            priceScaleId: scaleId,
            lastValueVisible: plot.lastValueVisible ?? false,
            priceLineVisible: plot.priceLineVisible ?? false,
            priceFormat: { type: "price" as any, precision: 2, minMove: 0.01 },
          });
        }

        result.push({ instanceId: active.instanceId, plotKey: plot.key, series });
      } catch (err) {
        console.error(`[IndicatorRenderer] Error creating series for ${active.indicatorId}.${plot.key}:`, err);
      }
    }

    return result;
  }

  private removeSeries(instanceId: string) {
    const series = this.seriesMap.get(instanceId);
    if (series && this.chart) {
      for (const s of series) {
        try { this.chart.removeSeries(s.series); } catch (_) {}
      }
    }
    this.seriesMap.delete(instanceId);
    this.paramsHash.delete(instanceId);
    this.dataCache.delete(instanceId);
    // Force layout recalc next time since a series was removed
    this.lastLayoutKey = '';
    this.lastScaleMargins.delete(instanceId);
  }

  removeAll() {
    const ids = Array.from(this.seriesMap.keys());
    for (const id of ids) {
      this.removeSeries(id);
    }
    this.seriesMap.clear();
    this.paramsHash.clear();
    this.dataCache.clear();
    this.metadataStore.clear();
    this.lastCandleCount = 0;
    this.lastLayoutKey = '';
    this.lastScaleMargins.clear();

    // Clear markers via plugin
    if (this.markersPlugin) {
      try { this.markersPlugin.setMarkers([]); } catch (_) {}
    }

    // Remove all price lines from candle series
    for (const pl of this.activePriceLines) {
      try { this.candleSeries?.removePriceLine(pl); } catch (_) {}
    }
    this.activePriceLines = [];

    // Remove all range price line primitives
    for (const { primitive, series } of this.activeRangePrimitives) {
      try { series.detachPrimitive(primitive); } catch (_) {}
    }
    this.activeRangePrimitives = [];

    // Remove the zone-band primitive
    if (this.activeBandsPrimitive) {
      try { this.candleSeries?.detachPrimitive(this.activeBandsPrimitive); } catch (_) {}
      this.activeBandsPrimitive = null;
    }

    // Remove the canvas shape primitives (curve bands / segments / labels)
    for (const { primitive, series } of this.activeShapesPrimitives) {
      try { series.detachPrimitive(primitive); } catch (_) {}
    }
    this.activeShapesPrimitives = [];
  }

  getSeriesCount(): number {
    let count = 0;
    for (const [, series] of this.seriesMap) {
      count += series.length;
    }
    return count;
  }
}
